import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const CONSENT_KEY = "cookie-consent";

/** Ícone exclusivo Berastays — escudo de privacidade com selo coral.
 *  Substitui o emoji genérico 🍪 por um símbolo coerente com a marca. */
const PrivacyShieldIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 28 28" fill="none" className={className} aria-hidden="true">
    <defs>
      <linearGradient id="cc-shield" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="hsl(12 82% 56%)" />
        <stop offset="100%" stopColor="hsl(12 82% 44%)" />
      </linearGradient>
      <linearGradient id="cc-glow" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="white" stopOpacity="0.35" />
        <stop offset="100%" stopColor="white" stopOpacity="0" />
      </linearGradient>
    </defs>
    <path
      d="M14 2.5 4.5 6.2v7.1c0 5.7 4 10.4 9.5 12.2 5.5-1.8 9.5-6.5 9.5-12.2V6.2L14 2.5Z"
      fill="url(#cc-shield)"
    />
    <path
      d="M14 2.5 4.5 6.2v7.1c0 5.7 4 10.4 9.5 12.2 5.5-1.8 9.5-6.5 9.5-12.2V6.2L14 2.5Z"
      fill="url(#cc-glow)"
    />
    <path
      d="m9.4 14.3 3.1 3.1 6.2-6.2"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CookieConsent = () => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) {
      const timer = setTimeout(() => setShowBanner(true), 1800);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = useCallback(() => {
    localStorage.setItem(CONSENT_KEY, "accepted");
    setShowBanner(false);
  }, []);

  const handleReject = useCallback(() => {
    localStorage.setItem(CONSENT_KEY, "rejected");
    setShowBanner(false);
  }, []);

  // Esc fecha (= recusar). Bloqueia o evento apenas quando o banner está visível.
  useEffect(() => {
    if (!showBanner) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleReject();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showBanner, handleReject]);

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          role="dialog"
          aria-modal="false"
          aria-labelledby="cookie-consent-title"
          aria-describedby="cookie-consent-desc"
          initial={{ opacity: 0, y: 24, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 14, scale: 0.98 }}
          transition={{ type: "spring", damping: 28, stiffness: 360 }}
          className="fixed z-50 left-4 right-4 bottom-[calc(5.5rem+env(safe-area-inset-bottom))] sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-[400px]"
        >
          {/* Halo coral subtil — adiciona profundidade premium sem ruído visual */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -inset-2 rounded-[28px] bg-[radial-gradient(120%_80%_at_0%_0%,hsl(12_82%_52%/0.18),transparent_60%)] blur-xl"
          />

          <div className="relative overflow-hidden rounded-2xl border border-border/60 bg-white/95 backdrop-blur-xl shadow-[0_20px_60px_-15px_rgba(15,23,42,0.25)]">
            {/* Faixa superior coral — assinatura de marca */}
            <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-[hsl(12_82%_52%)] via-[hsl(18_82%_60%)] to-[hsl(12_82%_52%)]" />

            <div className="p-5 sm:p-6">
              <div className="flex items-start gap-3.5">
                <div className="relative flex-shrink-0">
                  <div className="absolute inset-0 -m-1 rounded-2xl bg-[hsl(12_82%_52%)]/15 blur-md" />
                  <PrivacyShieldIcon className="relative w-10 h-10 drop-shadow-[0_4px_10px_rgba(232,81,61,0.35)]" />
                </div>
                <div className="min-w-0 flex-1">
                  <h2
                    id="cookie-consent-title"
                    className="font-display text-[15px] font-semibold tracking-tight text-foreground"
                  >
                    {t("cookies.title", "A tua privacidade importa")}
                  </h2>
                  <p
                    id="cookie-consent-desc"
                    className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground"
                  >
                    {t("cookies.description", "Usamos cookies para uma experiência segura, personalizada e sem falhas. Lê a nossa")}{" "}
                    <Link
                      to="/privacidade"
                      className="font-medium text-foreground underline decoration-[hsl(12_82%_52%)]/40 underline-offset-2 hover:decoration-[hsl(12_82%_52%)] transition-colors"
                    >
                      {t("cookies.privacyPolicy", "Política de Privacidade")}
                    </Link>
                    .
                  </p>
                </div>
              </div>

              {/* Ações premium — primária coral, secundária discreta */}
              <div className="mt-5 grid grid-cols-2 gap-2.5">
                <button
                  type="button"
                  onClick={handleReject}
                  className="group h-11 rounded-xl border border-border/70 bg-white text-[13px] font-semibold text-muted-foreground transition-all duration-200 hover:border-border hover:text-foreground hover:bg-muted/40 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/20"
                >
                  {t("cookies.reject", "Recusar")}
                </button>
                <button
                  type="button"
                  onClick={handleAccept}
                  autoFocus
                  className="group relative h-11 overflow-hidden rounded-xl bg-gradient-to-br from-[hsl(12_82%_56%)] to-[hsl(12_82%_46%)] text-[13px] font-semibold text-white shadow-[0_8px_20px_-6px_rgba(232,81,61,0.55)] transition-all duration-200 hover:shadow-[0_12px_26px_-6px_rgba(232,81,61,0.65)] hover:brightness-[1.03] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(12_82%_52%)]/40 focus-visible:ring-offset-2"
                >
                  <span className="relative z-10">{t("cookies.accept", "Aceitar")}</span>
                  {/* shimmer subtil no hover */}
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover:translate-x-full"
                  />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
